import { DataRoomStatus } from 'further-types/data-room';
import { ApiInputs, ApiResponses } from 'lib/trpcClient';
import {
  DataRoomFormData,
  defaultData,
  defaultInvestmentOpportunitiesContent,
} from './formData';

export const mapDataRoomResponseToFormData = (
  data: ApiResponses['dataRoom']['getById'],
): DataRoomFormData => {
  const customLogo = data.logoSettings?.customLogo;

  const common = {
    headline: data.headline ?? defaultData.headline,
    subheading: data.subheading ?? defaultData.subheading,
    contact: data.contact
      ? {
          name: data.contact.name,
          email: data.contact.email,
          phone: data.contact.phone,
          image: data.contact.image.objectKey
            ? {
                uri: data.contact.image.uri,
                objectKey: data.contact.image.objectKey,
              }
            : null,
        }
      : defaultData.contact,
    keyInformation:
      data.keyInformation.length > 0
        ? data.keyInformation
        : defaultData.keyInformation,
    logoSettings: {
      useCustomLogo: data.logoSettings?.useCustomLogo,
      customLogo: customLogo?.key
        ? { previewUri: customLogo.uri ?? '', objectKey: customLogo.key }
        : null,
    },
    legalTermsSettings:
      data.legalTermsSettings ?? defaultData.legalTermsSettings,
  };

  if (data.status === DataRoomStatus.Live) {
    return {
      ...common,
      status: data.status,
      investmentOpportunitiesContent: {
        title:
          data.investmentOpportunitiesContent?.title ??
          defaultInvestmentOpportunitiesContent.title,
        subtitle:
          data.investmentOpportunitiesContent?.subtitle ??
          defaultInvestmentOpportunitiesContent.subtitle,
        description:
          data.investmentOpportunitiesContent?.description ??
          defaultInvestmentOpportunitiesContent.description,
        metrics: data.investmentOpportunitiesContent?.metrics?.length
          ? data.investmentOpportunitiesContent.metrics
          : defaultInvestmentOpportunitiesContent.metrics,
      },
    };
  }

  return {
    ...common,
    status: data.status,
    investmentOpportunitiesContent: data.investmentOpportunitiesContent
      ? {
          ...data.investmentOpportunitiesContent,
          metrics: data.investmentOpportunitiesContent.metrics?.length
            ? data.investmentOpportunitiesContent.metrics
            : defaultInvestmentOpportunitiesContent.metrics,
        }
      : defaultData.investmentOpportunitiesContent,
  };
};

// When a metric list contains only a single empty entry (the default form
// state), treat it as empty so we don't persist a blank row to the backend.
const omitEmptySingleEntry = <T extends { fieldName: string; value: string }>(
  list: Array<T>,
): Array<T> =>
  list.length === 1 && !list[0].fieldName?.trim() && !list[0].value?.trim()
    ? []
    : list;

// Avoid persisting empty strings for optional fields — send undefined instead.
const emptyToUndefined = (value: string | undefined): string | undefined =>
  value?.trim() ? value : undefined;

export const mapDataRoomFormDataToUpdateRequest = (
  id: string,
  data: DataRoomFormData,
): ApiInputs['dataRoom']['update'] => {
  const common = {
    id,
    headline: data.headline,
    subheading: data.subheading,
    contact: {
      email: data.contact.email,
      name: data.contact.name,
      phone: data.contact.phone,
      image: data.contact.image ?? { objectKey: '' },
    },
    keyInformation: omitEmptySingleEntry(data.keyInformation),
    logoSettings: {
      useCustomLogo: data.logoSettings?.useCustomLogo,
      customLogo: data.logoSettings?.customLogo
        ? { objectKey: data.logoSettings.customLogo.objectKey }
        : { objectKey: '' },
    },
    legalTermsSettings: data.legalTermsSettings
      ? {
          ...data.legalTermsSettings,
          customAgreementTitle: emptyToUndefined(
            data.legalTermsSettings.customAgreementTitle,
          ),
          customAgreementContent: emptyToUndefined(
            data.legalTermsSettings.customAgreementContent,
          ),
        }
      : undefined,
  };

  if (data.status === DataRoomStatus.Live) {
    return {
      ...common,
      status: data.status,
      investmentOpportunitiesContent: {
        ...data.investmentOpportunitiesContent,
        metrics: data.investmentOpportunitiesContent.metrics
          ? omitEmptySingleEntry(data.investmentOpportunitiesContent.metrics)
          : undefined,
      },
    };
  }

  return {
    ...common,
    status: data.status,
    investmentOpportunitiesContent: data.investmentOpportunitiesContent
      ? {
          ...data.investmentOpportunitiesContent,
          title: emptyToUndefined(data.investmentOpportunitiesContent.title),
          subtitle: emptyToUndefined(
            data.investmentOpportunitiesContent.subtitle,
          ),
          description: emptyToUndefined(
            data.investmentOpportunitiesContent.description,
          ),
          metrics: data.investmentOpportunitiesContent.metrics
            ? omitEmptySingleEntry(data.investmentOpportunitiesContent.metrics)
            : undefined,
        }
      : undefined,
  };
};
